import { AnalyticsBrowser } from '@segment/analytics-next'
import axios, { AxiosResponse } from 'axios'

import { env } from '@js/utils/env'


export const segment = {
  install(app: {
    config: { globalProperties: { $segment?: AnalyticsBrowser } }
  }): void {
    const writeKey = env.segment.API_KEY
    if (!writeKey) {
      console.error('SEGMENT_API_KEY is not defined. Analytics will not be initialized.')
      return
    }
    const initSegment = () => {
      if (app.config.globalProperties.$segment) {
        // already initialized
        return
      }

      const analytics = AnalyticsBrowser.load({ writeKey })
      app.config.globalProperties.$segment = analytics
    }

    const disableSegment = () => {
      // Optional: you can do more here (e.g. reset user) if you want
      app.config.globalProperties.$segment = undefined
    }

    if (typeof window === 'undefined') {
      return
    }

    // 1) Try to initialize immediately if Cookiebot already knows consent state
    if (window.Cookiebot && window.Cookiebot.consent) {
      initSegment()
    }

    // 2) Listen for Cookiebot events:
    // - CookiebotOnConsentReady: consent loaded from cookie or just submitted
    // - CookiebotOnAccept: user has accepted cookies (also fires on pages when already accepted)
    window.addEventListener('CookiebotOnConsentReady', initSegment)
    window.addEventListener('CookiebotOnAccept', initSegment)

    // 3) If user declines/changes consent to exclude statistics, disable Segment
    window.addEventListener('CookiebotOnDecline', disableSegment)
  },
}

// types (client-side)
export interface Instance {
  appContext: {
    config: {
      globalProperties: {
        $segment?: AnalyticsBrowser
      }
    }
  }
}

interface SegmentEvent {
  event?: string
  options?: Record<string, unknown>
}

interface SegmentDetails {
  identify?: SegmentEvent
  track?: SegmentEvent
  page?: SegmentEvent
}

// client-side segment function
export async function segmentCalls(
  instance: Instance,
  segmentDetails: SegmentDetails,
): Promise<void> {
  try {
    const segment = instance?.appContext?.config?.globalProperties?.$segment
    if (!segment) {
      console.error('Segment is not initialized.')
      return
    }

    const userId = await getUserId()

    if (segmentDetails.identify) {
      const { options } = segmentDetails.identify
      if (userId) {
        segment.identify(userId, options)
      } else {
        segment.identify(options)
      }
    }
    if (segmentDetails.track) {
      const { event, options } = segmentDetails.track
      segment.track(event, options)
    }
    if (segmentDetails.page) {
      const { event, options } = segmentDetails.page
      segment.page(event, options)
    }
  } catch (error) {
    console.error('Error in segment tracking', error)
  }
}

// segment server-side code
const SEGMENT_API = 'https://api.segment.io/v1'

/**
 * Generic Segment server-side function.
 *
 * @param endpoint - Segment API endpoint (e.g., "/track", "/identify").
 * @param payload - Name of the event, traits/properties we want tracked, etc.
 * @returns Axios response or undefined if an error occurred.
 */

export async function segmentServerRequest<TResponse = unknown>(
  endpoint: string,
  payload: Record<string, unknown>,
): Promise<AxiosResponse<TResponse> | void> {

  const writeKey = env.segment.SERVER_API_KEY
  if (!writeKey) {
    console.error('SEGMENT_API_KEY is not defined. Analytics will not be initialized.')
    return
  }

  // get anonymousId from localStorage and add to payload
  let anonymousId: string | null = null
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const anonymousIdData = window.localStorage.getItem('ajs_anonymous_id')
      anonymousId = anonymousIdData ? JSON.parse(anonymousIdData) : null
    }
  } catch (err) {
    console.warn('Could not read ajs_anonymous_id from localStorage', err)
  }

  // there's no reason anonymousId shouldn't be available, but just in case
  if (anonymousId) {
    payload.anonymousId = anonymousId
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Basic ${btoa(writeKey + ':')}`,
  }
  try {
    await axios.post(
      `${SEGMENT_API}${endpoint}`,
      payload,
      { headers },
    )
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        console.error(`Segment API error [${endpoint}]:`, {
          status: error.response.status,
          data: error.response.data,
        })
      } else if (error.request) {
        console.error(`No response received from Segment API [${endpoint}]:`, error.request)
      } else {
        console.error(`Error in setting up request [${endpoint}]:`, error.message)
      }
    } else {
      console.error(`Unexpected error [${endpoint}]:`, error)
    }
  }
}

export async function segmentServerTrack(
  event: string,
  userId: string,
  properties: Record<string, unknown> = {},
  context?: Record<string, unknown>,
): Promise<void> {
  const payload: Record<string, unknown> = {
    event,
    userId,
    properties,
  }

  if (context) {
    payload.context = context
  }

  await segmentServerRequest('/track', payload)
}

export async function segmentServerIdentify(
  userId: string,
  traits: Record<string, unknown> = {},
  context?: Record<string, unknown>,
): Promise<void> {
  const payload: Record<string, unknown> = {
    userId,
    traits,
  }

  if (context) {
    payload.context = context
  }

  await segmentServerRequest('/identify', payload)
}

//----------- segment utility functions ------------//

export function getUTMParams(url: string): Record<string, string> {
  const params = new URLSearchParams(new URL(url).search)
  const utmParams: Record<string, string> = {}
  for (const [key, value] of params.entries()) {
    if (key.startsWith('utm_')) {
      // remove 'utm_' prefix (utm & page params won't have prefix by default, they're added where needed)
      utmParams[key.substring(4)] = value
    }
  }
  return utmParams
}

/**
 * @returns Promise<string> - get user's IP or fallback message
 */
export async function getUserIPAddress(): Promise<string> {
  try {
    const response = await axios.get('https://api.ipify.org?format=json', {
      timeout: 5000, // 5 second timeout
    })

    if (response.data && response.data.ip) {
      return response.data.ip
    } else {
      console.warn('IP address not found in response')
      return 'ip address not available'
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        console.warn('IP address request timed out')
      } else if (error.response) {
        console.warn(`IP address API error: ${error.response.status}`)
      } else if (error.request) {
        console.warn('No response received from IP address API')
      } else {
        console.warn(`Error setting up IP address request: ${error.message}`)
      }
    } else {
      console.warn('Unexpected error getting IP address:', error)
    }
    return 'ip address not available'
  }
}

/**
 * @returns string - use magento entity_id or 'unknown' if not available
 */
export async function getUserId(): Promise<string | null> {
  const customerData = localStorage.getItem('customer')

  if (customerData) {
    const customerObj = JSON.parse(customerData)
    return customerObj.entity_id || null
  }

  return null
}

/**
 * @returns object with page context data
 */
export function getPageParams(): Record<string, unknown> {
  const currentUrl = new URL(window.location.href)

  return {
    path: currentUrl.pathname,
    referrer: document.referrer || '',
    search: currentUrl.search,
    title: document.title,
    url: window.location.href,
  }
}
